/**
 * The guided walkthrough's script, built from the form the firm is actually running.
 *
 * THE FIRST VERSION OF THIS READ THE SHIPPED SCHEMA, and was wrong the day it was written. The
 * live form has ten of its shipped pages hidden, four renamed, a new page of thirty-five fields
 * added, and three pages made conditional. A walkthrough whose second step taught "Parties" —
 * a page nobody can see any more — would not merely be useless; it would be confidently
 * misleading, which is worse, and it would go on being so every time somebody edited the form.
 *
 * So the steps are GENERATED from the effective layout: the real pages, in the real order, under
 * the firm's own names. `GUIDANCE` holds what we can say about a page we recognise; anything else
 * — a page the firm invented — is described from its own title and description, which is what the
 * person who created it wrote for exactly this purpose.
 *
 * The fixed steps at each end are about the app rather than the form, so they do not change.
 */

/**
 * What each shipped page is for, keyed by section id.
 *
 * `body` is prose. `example` is what somebody actually typed, which is the part that makes this a
 * walkthrough rather than a table of contents. Both are omitted for pages the firm added, because
 * inventing an example for a page whose questions we have never seen would teach a fiction.
 */
/**
 * Marks a sentence that is only true when the approval workflow is on.
 *
 * The page descriptions had the same problem the closing steps did: "the practice group decides
 * which approvals the matter needs", "a yes here adds an approval", "approvers see what is
 * attached" — none of which happens with the workflow off. Rather than keeping two copies of every
 * description, the approval sentences are tagged and dropped.
 */
const APPROVALS = (sentence) => ({ __approvalsOnly: sentence });

/** Resolves the tagged sentences for the mode the firm is actually in. */
function resolveBody(body, workflowEnabled) {
  return (body || [])
    .map((line) => (line && line.__approvalsOnly ? (workflowEnabled ? line.__approvalsOnly : '') : line))
    .filter(Boolean);
}

const GUIDANCE = {
  parties: {
    body: [
      'Every name and entity that should go to the conflicts search. Completeness here matters more than anywhere else on the form.',
    ],
    example: [
      ['Client', 'Northgate Community College District'],
      ['Adverse party', 'A former classified employee'],
    ],
    tip: 'If you are unsure whether somebody belongs here, put them in. An extra name costs a search; a missing one costs a conflict nobody caught.',
  },
  header: {
    body: [
      'Who is responsible for the matter, which practice group it belongs to, and how it is set up.',
      APPROVALS('The practice group decides which approvals the matter needs, so it is worth getting right first time.'),
    ],
    example: [
      ['Responsible partner', 'Brian M. Wheeler'],
      ['Practice group', 'Student Services & Disabilities Law'],
    ],
    tip: 'The practice group leader fills in by itself once you choose the group.',
  },
  client: {
    body: [
      'The client and the matter. If the firm already acts for them, start typing the name and pick them from the list — the address and contact details come across rather than being retyped.',
    ],
    example: [
      ['Client', 'Northgate Community College District (existing)'],
      ['Address', 'Filled in from the firm’s records'],
    ],
    tip: 'Picking an existing client is the difference between one client record and two slightly different ones.',
  },
  representation: {
    body: ['What the firm is actually being asked to do, in a sentence or two.'],
    example: [['Description', 'Defence of a wage-and-hour claim brought by a former employee.']],
  },
  wallBuilder: {
    body: ['Who may access this file. Firm policy requires this to be answered deliberately rather than left at a default.'],
  },
  clientSource: {
    body: [
      'Where the work came from — including work introduced by a firm employee or a relative of one, which the firm wants recorded.',
      APPROVALS('Some answers add an approval: introduced work needs a partner to sign off before the file opens.'),
    ],
    example: [['Source', 'Existing client']],
    tip: 'Answer it honestly rather than conveniently. The question exists to protect you as much as the firm.',
  },
  priorRepresentation: {
    body: [
      'How the client came to the firm, and the risk questions that go with it — including whether anybody else acted for them on this matter first.',
    ],
    example: [
      ['Source of client', 'Existing client'],
      ['Previously represented', 'No'],
    ],
    tip: 'Answer it plainly. A previous lawyer on the same matter is exactly what this is here to catch.',
  },
  conflictCheck: {
    body: [
      'The structured party list the conflicts search runs against, and the questions about joint representation and waivers that go with it. Completeness here matters more than anywhere else on the form.',
    ],
    example: [
      ['Parties checked', 'Client and the adverse party'],
      ['Result', 'Attached, no conflict found'],
    ],
  },
  matterType: {
    body: [
      'Litigation or non-litigation — one or the other, never both. Your answer opens the matching page and hides the other, so you are not answering trial questions on an advice matter.',
    ],
    example: [['Type', 'Litigation']],
  },
  litigation: {
    body: ['The court, the case number, and who is on the other side. Shown only when the matter is litigation.'],
    example: [
      ['Court', 'Los Angeles Superior Court'],
      ['Opposing counsel', 'Named'],
    ],
  },
  nonLitigation: {
    body: ['The advice or transactional detail, including anybody adverse. Shown only when the matter is non-litigation.'],
  },
  fees: {
    body: ['The fee arrangement and what the matter is expected to be worth.'],
    example: [['Estimated total', '$45,000']],
  },
  litigationHold: {
    body: ['Whether a litigation hold letter is needed, and who it goes to.'],
  },
  areasOfLaw: {
    body: ['How the matter is classified for reporting. The firm maintains this list.'],
  },
  filesToBeOpened: {
    body: ['The matter name that will appear on the file, and what the file should contain.'],
    example: [['Matter name', 'Defence of wage-and-hour claim — Reyes']],
  },
  origination: {
    body: [
      'Who originated the work and who will do it. Where origination is shared, the percentages have to add to 100 — the form adds them up and will not let a split through that does not.',
    ],
    example: [['Originating', 'Wheeler 60% · Delgado 40%']],
  },
  attachments: {
    body: [
      'The conflicts documentation and the signed engagement letter. A missing letter is the commonest thing anybody comes back about.',
    ],
  },
};

/** The steps before any page: what this is, and where a matter starts. */
const OPENING = [
  {
    id: 'intro',
    title: 'A practice run at opening a matter',
    body: [
      'This is the real form, page by page, with nothing behind it. Type into it, open the dropdowns, answer a question and watch the next one appear — it behaves exactly as it will when you do this for real.',
      '**Nothing is saved.** No draft is created, nothing is submitted, and everything you type here disappears when you close it. Your own matters are untouched.',
    ],
    note: 'Five minutes if you take your time. You can leave at any point and replay it from Help.',
  },
  {
    id: 'start',
    title: 'Start from New matter',
    body: [
      'For real, everything begins at **New matter** in the left rail. A draft is created as soon as you start typing and saves as you go — there is no Save button to forget, and closing the tab loses nothing.',
      'Your drafts and completed matters are all under **My matters**. What follows is the form that opens.',
    ],
    example: [['Draft created', 'Saves automatically — no Save button to forget']],
  },
];

/**
 * The steps after the last page: what finishing does, and what you get.
 *
 * A FUNCTION, NOT A CONSTANT, because what happens on submit is a setting. With the approval
 * workflow off — as it is here — a matter produces a PDF and goes nowhere; with it on, it routes.
 * The first version of these steps taught approval chains, a first approver emailed straight away
 * and a status reading "Awaiting Practice Group Leader", to a firm where none of that happens.
 */
function closingSteps({ workflowEnabled, submitLabel } = {}) {
  const button =
    String(submitLabel || '').trim() || (workflowEnabled ? 'Submit for approval' : 'Send / Download PDF');

  const validation = {
    id: 'validation',
    title: 'Before you finish',
    body: [
      'That is the whole form. For real, it tells you what is still missing before you press anything, and takes you straight to it.',
      'Some questions are **required** and block you. Others are **usually provided**: you can leave one blank, but you are asked to say briefly why, and that reason is kept with the record. It is not a way around the question — it is how you answer honestly when there is nothing to give.',
    ],
    example: [['Still needed', 'Engagement letter — or a line saying why there is none']],
  };

  const done = (extra) => ({
    id: 'done',
    title: 'That is the whole thing',
    body: [
      ...extra,
      'Nothing you typed in this practice run was saved. When you are ready for a real one, it is **New matter** in the left rail.',
      'The **Help** page has all of this in writing.',
    ],
    note: 'You will not be shown this automatically again. Replay it any time from Help.',
  });

  if (!workflowEnabled) {
    return [
      validation,
      {
        id: 'submit',
        title: 'What you get at the end',
        body: [
          `You are asked to check your answers one last time, and then **${button}** completes the matter and produces the **PDF** — the full record of everything you entered.`,
          'It is emailed to you with the PDF attached, and it stays on the matter under **My matters**, so you can download it again whenever you need it.',
          '**Nothing is routed anywhere.** Nobody is asked to approve it, and no email goes to anybody but you. What happens to the PDF next is up to you.',
        ],
        example: [['Result', 'A PDF of the completed form, emailed to you and kept on the matter']],
      },
      done([]),
    ];
  }

  return [
    validation,
    {
      id: 'submit',
      title: 'What happens when you submit',
      body: [
        `**${button}** stops the matter being yours to edit and starts it moving. The approvals it needs are worked out from your answers and from your practice group's own routing, and the first approver is emailed straight away.`,
        'They can approve from the email itself without signing in, which is why approvals usually come back the same day.',
      ],
      example: [
        ['Step 1', 'Intake conflict check'],
        ['Step 2', 'Practice Group Leader'],
        ['Step 3', 'Responsible Partner'],
      ],
    },
    {
      id: 'tracking',
      title: 'Following it afterwards',
      body: [
        'Open the matter from **My matters** to see where it is: who has approved, who it is sitting with, and everything anybody has said about it.',
        'If an approver asks for more information, it comes back to you with their question attached. You answer it and it carries on from where it stopped rather than starting again.',
      ],
      example: [['Status', 'Awaiting Practice Group Leader']],
    },
    done([
      'Once every approval is in, the matter is approved, the file can be opened, and the full record — every answer, every approval, every comment — is kept as a PDF.',
    ]),
  ];
}

/**
 * A page the firm added, described from what they wrote on it.
 *
 * NOTHING HERE IS INVENTED. The description and the question labels are the firm's own words,
 * typed by whoever built the page for exactly this purpose. Naming the first few questions is
 * what keeps this a walkthrough rather than a shrug: the firm's largest added page carries
 * forty-odd questions, and "a page your firm added" tells somebody facing it nothing at all.
 */
function describeUnknownSection(section) {
  const questions = (section.fields || []).filter((f) => f.type !== 'notice' && f.label);
  const names = questions.slice(0, 4).map((f) => f.label.trim().replace(/[:?]$/, ''));
  const count = questions.length;

  const lead =
    section.description ||
    'A page your firm added to the form. The questions on it are the firm’s own, so there is no worked example for it here.';

  const inventory = !count
    ? ''
    : `There ${count === 1 ? 'is 1 question' : `are ${count} questions`} on this page${
        names.length ? `, starting with ${listToProse(names)}` : ''
      }.`;

  return { body: [lead, inventory].filter(Boolean) };
}

/** "a, b and c" — an Oxford-comma-free list, because these are read as a sentence. */
function listToProse(items) {
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** What we can say about one page: recognised from the schema, or read off the firm's own page. */
function describe(section, workflowEnabled) {
  const known = GUIDANCE[section.id];
  if (!known) return describeUnknownSection(section);
  return { ...known, body: resolveBody(known.body, workflowEnabled) };
}

/** True when the page is not always shown, so the guide can say so rather than leaving a gap. */
const isConditional = (section) => section.visibleIf !== undefined && section.visibleIf !== true;

/**
 * The walkthrough for the form as configured.
 *
 * @param {object[]} effectiveSections  from buildEffectiveSections(FORM_SECTIONS, fieldLayout) —
 *                                      the same list the intake form itself renders, so the tour
 *                                      cannot describe a page that is not there.
 * @param {object}  [options]
 * @param {boolean} [options.workflowEnabled]  whether submitting routes for approval or simply
 *                                             produces the PDF. Defaults to OFF, because a tour
 *                                             that wrongly promises approvals is worse than one
 *                                             that wrongly omits them: somebody waits for an email
 *                                             that is never coming.
 * @param {string}  [options.submitLabel]      the real label on the submit button
 */
export function buildWalkthroughSteps(effectiveSections, options = {}) {
  const sections = Array.isArray(effectiveSections) ? effectiveSections : [];

  const pageSteps = sections.map((section) => ({
    id: `page:${section.id}`,
    // The firm's own name for the page, never the one that shipped.
    title: section.title || 'Untitled page',
    // The page itself, so the walkthrough can render it rather than describe it. This is what
    // makes it a practice run: the real fields, in the real order, with the real dropdowns.
    section,
    ...describe(section, options.workflowEnabled),
    // Said on the page itself rather than buried, because a conditional page not appearing is
    // otherwise indistinguishable from one that is missing.
    note: isConditional(section)
      ? 'This page only appears for some matters, depending on your earlier answers.'
      : undefined,
  }));

  return [...OPENING, ...pageSteps, ...closingSteps(options)];
}

/**
 * The same pages as a written list, for the Help page.
 *
 * Shares `GUIDANCE` with the tour deliberately: two descriptions of the same page, maintained
 * separately, is how one of them ends up wrong.
 */
export function buildPageGuide(effectiveSections, options = {}) {
  const sections = Array.isArray(effectiveSections) ? effectiveSections : [];
  return sections.map((section) => ({
    id: section.id,
    title: section.title || 'Untitled page',
    description: describe(section, options.workflowEnabled).body.filter(Boolean).join(' '),
    conditional: isConditional(section),
  }));
}

export { GUIDANCE, OPENING, closingSteps };
